import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form, Field, FormikValues } from "formik";
import { CircularProgress, MenuItem, TextField } from "@mui/material";
import "../config.styles.css";
import * as yup from "yup";
import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import { HERO_CAROUSEL, UNIQUE_NAME_ERROR } from "../LandingPage/constants";
import { useNavigate } from "react-router";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(1),
    },
}));

export interface DialogTitleProps {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle align="center" sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

export default function CloneContentVersion({
    show,
    handleCloseCB,
    formData,
}: any) {
    const navigate = useNavigate();

    const [open, setOpen] = useState(show);
    const [submitting, setSubmitting] = useState(false);
    const [masterData, setMasterData] = useState<any>([]);
    const { enqueueSnackbar } = useSnackbar();
    const [isEditRail, setIsEditRail] = useState(false);
    const [initialValues, setInitialValues] = useState<any>(null);
    const [loader, setLoader] = useState(false);
    const [errorMessage, setErrorMessage] = useState<any>(null);

    useEffect(() => {
        getMasterInfo();
    }, []);

    useEffect(() => {
        if (formData) {
            setInitialValues({
                template: formData?.template,
                versionName: "",
                canonicalName: "",
                items: formData?.items || [],
                publishStatus: "draft",
            });
        }
    }, [formData]);

    const navigateToPageDetails = (id: string) => {
        navigate("/content-curation-list?pageName=" + id);
    };

    const cloneVersionAPI = async (value: any) => {
        try {
            setLoader(true);
            setErrorMessage(null);
            const saveVersion = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/page/versions?action=CLONED`,
                value
            );
            if (saveVersion?.data?.success) {
                navigateToPageDetails(saveVersion?.data?.data?.id);
                enqueueSnackbar(saveVersion?.data?.message, {
                    variant: "success",
                });
                handleCloseCB();
            } else {
                enqueueSnackbar(saveVersion?.data?.message, {
                    variant: "error",
                });
            }
        } catch (error: any) {
            if (
                error.response?.data?.message &&
                error.response?.data?.message.includes("already exists")
            ) {
                setErrorMessage(UNIQUE_NAME_ERROR);
            } else {
                enqueueSnackbar(error.response?.data?.message, {
                    variant: "warning",
                });
            }
        } finally {
            setLoader(false);
        }
    };

    const handleClose = () => {
        handleCloseCB();
    };

    // on submit form
    const handleOnSubmit = async (values: FormikValues) => {
        cloneVersionAPI(values);
    };

    // validation schema
    const validationSchema = yup.object({
        versionName: yup.string().required("Version name is mandatory"),
        canonicalName: yup
            .string()
            .required("Canonical name is mandatory")
            .min(8, "Canonical name must consist of at least 8 characters.")
            .max(20, "Canonical name must not exceed 20 characters in length")
            .matches(/^[a-zA-Z0-9]+$/, {
                message:
                    "Canonical name must consist of only numbers and letters",
            }),
    });

    // call API for getting the master information
    const getMasterInfo = async () => {
        try {
            const materInformation = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/page/master`
            );
            if (materInformation.data?.success) {
                setMasterData(materInformation.data?.data?.pageTemplate);
            }
        } catch (error) {
            console.log("Error in getting master information", error);
        }
    };

    return (
        <div>
            {/* This is a popup modal for handling save config */}
            <BootstrapDialog
                fullWidth
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
            >
                <BootstrapDialogTitle
                    id="customized-dialog-title"
                    onClose={handleClose}
                >
                    {"Confirm Version Cloning"}
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    {/* Form contents for handling save configuration starts here */}
                    <div className="mb-1">
                        Version name :{" "}
                        <span className="f-bold">{formData?.versionName}</span>
                    </div>
                    <div className="mb-1">
                        Canonical name :{" "}
                        <span className="f-bold">
                            {formData?.canonicalName}
                        </span>{" "}
                    </div>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        enableReinitialize
                        onSubmit={handleOnSubmit}
                    >
                        {({ values, handleChange, touched, errors }: any) => (
                            <Form className="config-form">
                                {/* <h3>Version Name</h3>
                                <h3>Canonical Name</h3> */}

                                <Field
                                    name="versionName"
                                    as={TextField}
                                    label="Name"
                                    value={values?.versionName || ""}
                                    placeholder="Name"
                                    onChange={handleChange}
                                    error={
                                        touched.versionName &&
                                        errors.versionName
                                    }
                                    helperText={
                                        (errors.versionName &&
                                            errors.versionName) ||
                                        "*Display Name"
                                    }
                                />
                                <>
                                    <Field
                                        name="canonicalName"
                                        as={TextField}
                                        label="Canonical Name"
                                        value={values?.canonicalName || ""}
                                        placeholder="Canonical Name"
                                        onChange={(e: any) => {
                                            handleChange(e);
                                            setErrorMessage(null);
                                        }}
                                        error={
                                            (touched.canonicalName &&
                                                errors.canonicalName) ||
                                            errorMessage
                                        }
                                        helperText={
                                            errors.canonicalName
                                                ? errors.canonicalName
                                                : (errorMessage &&
                                                      errorMessage) ||
                                                  "*Canonical name is the unique identifier of the version. It must be between 8-20 characters in length, consisting of letters and numbers."
                                        }
                                    />
                                </>
                                <p className="cloning-bottom-text">
                                    Cloning a version will save it as a draft in
                                    the version listing page
                                </p>

                                <div className="d-flex j-center">
                                    <Button
                                        className="max-content-width float-center color-grey-bg"
                                        onClick={handleCloseCB}
                                        variant="contained"
                                        color="error"
                                    >
                                        {"Cancel"}
                                    </Button>

                                    <Button
                                        className="max-content-width float-center ml-1 primary"
                                        variant="contained"
                                        type="submit"
                                        disabled={loader}
                                    >
                                        {loader && (
                                            <CircularProgress
                                                size={20}
                                                color="primary"
                                            />
                                        )}{" "}
                                        {"Confirm"}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                    {/* Form contents for handling save configuration ends here */}
                </DialogContent>
            </BootstrapDialog>
        </div>
    );
}
